import { useContext } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import styles from './SbSharedRelatedItems.scss';
import { createFragmentContainer, graphql } from 'react-relay';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import {
    SbSharedThinBrowseItemTrackingProvider,
    SbSharedThinBrowseItemTrackingContext,
} from '../SbSharedThinBrowseItemTrackingProvider/SbSharedThinBrowseItemTrackingProvider';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

const SbSharedRelatedItemsComponent = ({ itemSearch, user, currency, isClient, viewer }) => {
    const relatedItems = itemSearch?.relatedItems?.edges || [];
    const isVerifiedTrade = user?.isVerifiedTrade;
    const { isSeoQuicklookEnabled } = viewer;
    const { fireThinBrowseItemsImpressionTracking, fireItemClickTracking } = useContext(
        SbSharedThinBrowseItemTrackingContext
    );

    const title = (
        <FormattedMessage id="sb.SbSharedRelatedItems.title" defaultMessage="Related Items" />
    );

    if (relatedItems.length > 0) {
        return (
            <ServerVarsContextConsumer>
                {({ isMobile }) => (
                    <>
                        <BarHeader title={title} />
                        <QuickViewProvider>
                            {({ quickView }) => {
                                const quickViewProps = isSeoQuicklookEnabled ? quickView : null;

                                return (
                                    <div className={styles.container} data-tn="related-items">
                                        {relatedItems.map(({ node }, index) => (
                                            <SharedItemTile
                                                isClient={isClient}
                                                key={node.serviceId}
                                                index={index}
                                                item={node}
                                                currency={currency}
                                                isVerifiedTrade={isVerifiedTrade}
                                                showSellerName={false}
                                                viewer={viewer}
                                                quickViewProps={quickViewProps}
                                                itemSearch={itemSearch}
                                                isMobile={isMobile}
                                                onContentVisible={() =>
                                                    fireThinBrowseItemsImpressionTracking(index)
                                                }
                                                onClick={() => fireItemClickTracking(index)}
                                            />
                                        ))}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    </>
                )}
            </ServerVarsContextConsumer>
        );
    }
    return null;
};

SbSharedRelatedItemsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    viewer: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isClient: PropTypes.bool.isRequired,
};

const SbSharedRelatedItemsConsumer = ({ itemSearch, user, currency, isClient, viewer }) => {
    const relatedItems = itemSearch?.relatedItems?.edges || [];
    if (relatedItems.length === 0) {
        return null;
    }

    return (
        <SbSharedThinBrowseItemTrackingProvider
            itemSearch={itemSearch}
            viewer={viewer}
            items={relatedItems.map(edge => (edge ? edge.node : null))}
            thinBrowseListingType="related"
        >
            <SbSharedRelatedItemsComponent
                itemSearch={itemSearch}
                user={user}
                currency={currency}
                isClient={isClient}
                viewer={viewer}
            />
        </SbSharedThinBrowseItemTrackingProvider>
    );
};

SbSharedRelatedItemsConsumer.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    viewer: PropTypes.object,
    currency: PropTypes.string.isRequired,
    isClient: PropTypes.bool.isRequired,
};

export const SbSharedRelatedItems = createFragmentContainer(SbSharedRelatedItemsConsumer, {
    itemSearch: graphql`
        fragment SbSharedRelatedItems_itemSearch on ItemSearchQueryConnection {
            ...SharedItemTile_itemSearch
            ...SbSharedThinBrowseItemTrackingProvider_itemSearch
            relatedItems: thinBrowseRelatedItems(first: 12, seller: INCLUDE) {
                edges {
                    node {
                        serviceId
                        ...SharedItemTile_item
                        ...SbSharedThinBrowseItemTrackingProvider_items
                        ...freeShippingHelpers_item
                    }
                }
            }
        }
    `,
    user: graphql`
        fragment SbSharedRelatedItems_user on User {
            isVerifiedTrade
        }
    `,
    viewer: graphql`
        fragment SbSharedRelatedItems_viewer on Viewer {
            isSeoQuicklookEnabled: isEnabledByLocale(featureName: "SEO_QUICKLOOK")
            ...SharedItemTile_viewer
            ...SbSharedThinBrowseItemTrackingProvider_viewer
        }
    `,
});
